import { css, cva } from '@styled-system/css';
import type { FC } from 'react';
import { useTranslation } from 'react-i18next';

import { SELL_HISTORY_TABS, type SellHistoryTabValue } from '../types';

// ============================================================================
// Sélecteur segmenté Ventes / Retirés / Invendus — boutons dans une pilule
// (remplace les tabs soulignés v1).
// ============================================================================

// Boutons discrets avec pastille de statut colorée — langage « filtre de
// contenu », distinct de la navigation principale (pilule segmentée).
const segmentedRoot = css({
  display: 'inline-flex',
  alignItems: 'center',
  gap: '2',
  width: 'fit-content',
});

const segmentButton = cva({
  base: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '3',
    paddingY: '2.5',
    paddingX: '6',
    fontSize: 'bodySm',
    fontFamily: 'sans',
    fontWeight: 'medium',
    borderRadius: 'md',
    borderWidth: '1px',
    borderStyle: 'solid',
    borderColor: 'transparent',
    background: 'transparent',
    color: 'text.tertiary',
    cursor: 'pointer',
    transition: 'background 120ms ease, color 120ms ease, border-color 120ms ease',
    whiteSpace: 'nowrap',
  },
  variants: {
    active: {
      true: { fontWeight: 'semibold' },
      false: {
        _hover: { color: 'text.primary', bg: 'surface.subtle' },
      },
    },
    tone: {
      sold: {},
      removed: {},
      unsold: {},
      onSale: {},
    },
  },
  compoundVariants: [
    {
      active: true,
      tone: 'sold',
      css: { bg: 'success.soft', color: 'success.default', borderColor: 'success.border' },
    },
    {
      active: true,
      tone: 'removed',
      css: { bg: 'warning.soft', color: 'warning.default', borderColor: 'warning.border' },
    },
    {
      active: true,
      tone: 'unsold',
      css: { bg: 'surface.sunken', color: 'text.primary', borderColor: 'border.default' },
    },
    {
      active: true,
      tone: 'onSale',
      css: { bg: 'info.soft', color: 'info.default', borderColor: 'info.border' },
    },
  ],
  defaultVariants: { active: false },
});

const statusDot = cva({
  base: {
    width: '7px',
    height: '7px',
    borderRadius: 'full',
    flexShrink: 0,
  },
  variants: {
    tone: {
      sold: { bg: 'success.default' },
      removed: { bg: 'warning.default' },
      unsold: { bg: 'text.tertiary' },
      onSale: { bg: 'info.default' },
    },
  },
});

interface SellHistoryTabsProps {
  activeTab: SellHistoryTabValue;
  onTabChange: (tab: SellHistoryTabValue) => void;
  /** Liste des onglets — `label` est une clé i18n (produit par défaut ; VENDOR_SELL_HISTORY_TABS pour vendeur). */
  tabs?: ReadonlyArray<{ readonly value: SellHistoryTabValue; readonly label: string }>;
}

export const SellHistoryTabs: FC<SellHistoryTabsProps> = ({ activeTab, onTabChange, tabs = SELL_HISTORY_TABS }) => {
  const { t } = useTranslation();

  return (
    <div className={segmentedRoot} role="tablist">
      {tabs.map((tab) => (
        <button
          key={tab.value}
          type="button"
          role="tab"
          aria-selected={activeTab === tab.value}
          className={segmentButton({ active: activeTab === tab.value, tone: tab.value })}
          onClick={() => onTabChange(tab.value)}
        >
          <span className={statusDot({ tone: tab.value })} />
          {t(tab.label)}
        </button>
      ))}
    </div>
  );
};
